import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/lib/api/types';
import type { UserInfo } from '@/types/user';
import { fetchUsers } from './api';

export type UsersErrorKey = 'users.errorForbidden' | 'users.errorLoad';

/** What one completed load produced, tagged with the load it answered. */
interface Outcome {
  nonce: number;
  users: UserInfo[];
  errorKey: UsersErrorKey | null;
}

export interface UsersState {
  users: UserInfo[];
  isLoading: boolean;
  errorKey: UsersErrorKey | null;
  /** Re-reads the whole list from the server. */
  reload: () => void;
  /**
   * Folds a user the server just returned back into the list.
   *
   * Every write answers with the saved `UserInfo`, so re-reading the list to learn
   * what was just handed back would be a round trip for an answer already in hand —
   * and it would blank the row the reader is looking at while it ran.
   */
  apply: (user: UserInfo) => void;
}

/**
 * The user list.
 *
 * `GET /user/all` takes no filter, no sort and no page — it returns every user in one
 * response. So this hook's only job is that one request, and the narrowing, ordering
 * and counting all happen over the array it produces.
 *
 * **Loading is derived, not stored.** The state is the outcome of the last completed
 * load tagged with the load it answered; "loading" is that tag not matching the load
 * being asked for. A `setLoading(true)` at the top of the effect — the obvious way to
 * write this — sets state during the commit and cascades an extra render.
 */
export const useUsers = (): UsersState => {
  const [nonce, setNonce] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchUsers(controller.signal)
      .then((users) => {
        setOutcome({ nonce, users, errorKey: null });
      })
      .catch((caught: unknown) => {
        // An abort is not an error: a newer load is already in flight and its
        // outcome is the one that should win.
        if (controller.signal.aborted) return;

        setOutcome({
          nonce,
          users: [],
          // A 403 is not a failure to retry — every role but ADMIN is refused here,
          // and telling them to check their connection sends them looking in the
          // wrong place.
          errorKey:
            caught instanceof ApiError && caught.isForbidden
              ? 'users.errorForbidden'
              : 'users.errorLoad',
        });
      });

    return () => controller.abort();
  }, [nonce]);

  const reload = useCallback(() => setNonce((current) => current + 1), []);

  const hasList = outcome !== null;

  const apply = useCallback(
    (saved: UserInfo) => {
      // Nothing to fold into yet: the first load is still in flight and may have
      // left before this write, so it cannot be trusted to contain the result.
      // Starting a new one discards it — a stale nonce loses — and the list that
      // lands is guaranteed to include what was just saved.
      if (!hasList) {
        setNonce((current) => current + 1);
        return;
      }

      setOutcome((current) => {
        if (!current) return current;

        const known = current.users.some((user) => user.id === saved.id);

        return {
          ...current,
          // A created user goes in at the front; the table sorts the array itself,
          // so where it is inserted is not where it appears.
          users: known
            ? current.users.map((user) => (user.id === saved.id ? saved : user))
            : [saved, ...current.users],
        };
      });
    },
    [hasList],
  );

  const isCurrent = outcome?.nonce === nonce;

  return {
    // The list stays on screen while a reload runs, so a refresh does not blank the
    // table under the reader. An error clears it: yesterday's answer under an error
    // message is worse than none.
    users: outcome?.errorKey ? [] : (outcome?.users ?? []),
    isLoading: !isCurrent,
    errorKey: isCurrent ? outcome.errorKey : null,
    reload,
    apply,
  };
};
